"use client";
import { Card, Table, Form, Badge, Spinner, Alert } from "react-bootstrap";
import { formatCurrency, formatDate } from "../../utils";
import { useOrders, Order } from "./hooks/use-orders";
import { getOrderStatusLabel } from "../../utils/getOrderStatusLabel";
import { getStatusBadgeVariant } from "../../utils/getStatusBadgeVariant";
import { PaymentProofModal } from "./OrderDetails/PaymentProofModal"; 
import { useState } from "react";

const OrdersTable = () => {
  const {
    searchQuery,
    paginatedOrders,
    isLoading,
    isError,
    error,
    handleSearchChange,
    handleOrderClick,
  } = useOrders();
  
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showProofModal, setShowProofModal] = useState(false);

  const handleOpenProofModal = (order: Order) => {
    setSelectedOrder(order);
    setShowProofModal(true);
  };

  const handleCloseProofModal = () => {
    setSelectedOrder(null);
    setShowProofModal(false);
  };
  return (
    <>
      <Card className="border-0 rounded-3">
        <Card.Body className="p-0">
          {isError ? (
            <Alert variant="danger" className="m-4">
              Error al cargar las órdenes: {error?.message || "Error desconocido"}
            </Alert>
          ) : (
            <div className="default-table-area">
              <div className="table-responsive">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 p-4">
                  <div className="d-flex align-items-center gap-3">
                    <div className="position-relative">
                      <Form.Control
                        type="search"
                        className="ps-5"
                        placeholder="Buscar órdenes"
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                      <span className="material-symbols-outlined position-absolute top-50 translate-middle-y ms-2">
                        search
                      </span>
                    </div>
                  </div>
                </div>

                <Table className="align-middle">
                  <thead>
                    <tr>
                      <th scope="col">Orden ID</th>
                      <th scope="col">Productos</th>
                      <th scope="col">Fecha</th>
                      <th scope="col">Puntos</th>
                      <th scope="col">Total</th>
                      <th scope="col">Estado</th>
                      <th scope="col">Pago</th>
                      <th scope="col">Acciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="text-center">
                          <Spinner animation="border" variant="primary" />
                          <span className="ms-2">Cargando órdenes...</span>
                        </td>
                      </tr>
                    ) : paginatedOrders.length > 0 ? (
                      paginatedOrders.map((order: Order) => (
                        <tr key={order.id}>
                          <td>
                            <span className="text-primary fw-medium">
                              #{order.id}
                            </span>
                          </td>

                          <td>
                            <div className="d-flex align-items-center">
                              <div className="ms-2 ps-1">
                                <h6 className="fw-medium fs-14 m-0">
                                  {order.items.length}{" "}
                                  {order.items.length === 1 ? "producto" : "productos"}
                                </h6>
                                <small className="text-muted">
                                  {order.items[0]?.product_id?.title || "Producto"}
                                  {order.items.length > 1
                                    ? ` y ${order.items.length - 1} más`
                                    : ""}
                                </small>
                              </div>
                            </div>
                          </td>

                          <td>{formatDate(order.created_at)}</td>

                          <td>
                            {order.items.reduce(
                              (total, item) =>
                                total + item.quantity * item.loyalty_points,
                              0
                            )}
                          </td>

                          <td>{formatCurrency(order.total, order.currency ?? "COP")}</td>

                          <td>
                            <Badge
                              bg={getStatusBadgeVariant(order.status)}
                              className="p-2 fs-12 fw-medium text-capitalize"
                            >
                              {getOrderStatusLabel(order.status)}
                            </Badge>
                          </td>

                          <td>
                            <Badge
                              style={{ fontSize: "12px" }}
                              bg={
                                order.is_paid
                                  ? "success"
                                  : order.status === "CANCELLED"
                                  ? "danger"
                                  : "warning"
                              }
                              className="p-2 fs-12 fw-medium"
                            >
                              {order.is_paid
                                ? "Aprobado"
                                : order.status === "CANCELLED"
                                ? "Cancelado"
                                : "Pendiente"}
                            </Badge>
                          </td>

                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {/* 🔹 Botón para ver detalles */}
                              <button
                                className="ps-0 border-0 bg-transparent lh-1"
                                onClick={() => handleOrderClick(order)}
                                title="Ver detalles"
                              >
                                <span className="material-symbols-outlined fs-16 text-primary">
                                  visibility
                                </span>
                              </button>
                                {/* 🔹 Botón para abrir comprobante si aplica */}
                              {order.payment_method === "TRANSFER" && (
                                <button
                                  className="ps-0 border-0 bg-transparent lh-1"
                                  onClick={() => handleOpenProofModal(order)}
                                  title={
                                    order.payment_proof
                                      ? "Ver comprobante"
                                      : "Subir comprobante"
                                  }
                                >
                                  <span className="material-symbols-outlined fs-16 text-success">
                                    receipt_long
                                  </span>
                                </button>
                              )}
                              {/* 🔹 Botón para abrir factura de Siigo si aplica */}
                              {order.siigo_invoice?.public_url && (
                                <button
                                  className="ps-0 border-0 bg-transparent lh-1"
                                  onClick={() => window.open(order.siigo_invoice?.public_url, '_blank')}
                                  title="Ver factura electrónica"
                                >
                                  <span className="material-symbols-outlined fs-16 text-info">
                                    description
                                  </span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="text-center">
                          {searchQuery
                            ? `No se encontraron resultados para "${searchQuery}".`
                            : "No hay órdenes disponibles."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {selectedOrder && (
        <PaymentProofModal
          isOpen={showProofModal}
          toggle={handleCloseProofModal}
          order={selectedOrder}
        />
      )}

    </>
  );
};

export default OrdersTable;
